import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CircleNotificationsOutlinedIcon from "@mui/icons-material/CircleNotificationsOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import useNotifications from "../notifications/useNotifications";
import { resolveNotificationLink } from "../notifications/notificationLinks";

// เลือกไอคอน/สีตามหมวด (Category = SaleOrderLog.TypeName หรืออื่น ๆ ในอนาคต)
function categoryVisual(category = "") {
  const c = category.toLowerCase();
  if (c.includes("cost") || c.includes("payment") || c.includes("expense"))
    return { icon: <PaidOutlinedIcon fontSize="small" />, color: "#2e7d32", bg: "#e8f5e9" };
  if (c.includes("file"))
    return { icon: <AttachFileOutlinedIcon fontSize="small" />, color: "#6a1b9a", bg: "#f3e5f5" };
  if (c.includes("ship") || c.includes("train") || c.includes("transport"))
    return { icon: <LocalShippingOutlinedIcon fontSize="small" />, color: "#00695c", bg: "#e0f2f1" };
  if (c.includes("sale order") || c.includes("booking"))
    return { icon: <DescriptionOutlinedIcon fontSize="small" />, color: "#1565c0", bg: "#e3f2fd" };
  return { icon: <CircleNotificationsOutlinedIcon fontSize="small" />, color: "#455a64", bg: "#eceff1" };
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 0) return "เมื่อสักครู่";
  if (diff < 60) return "เมื่อสักครู่";
  if (diff < 3600) return `${Math.floor(diff / 60)} นาทีที่แล้ว`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ชั่วโมงที่แล้ว`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} วันที่แล้ว`;
  return d.toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric" });
}

export default function NotificationBell() {
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { items, unreadCount, loading, markRead, markAllRead } = useNotifications();

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (n) => {
    if (!n.IsRead) markRead(n.NotificationId);
    const link = resolveNotificationLink(n);
    handleClose();
    if (link) history.push(link);
  };

  return (
    <>
      <Tooltip title="การแจ้งเตือน">
        <IconButton onClick={handleOpen} size="large" sx={{ color: "#5f6368" }}>
          <Badge
            badgeContent={unreadCount}
            max={99}
            color="error"
            overlap="circular"
          >
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: "92vw",
            borderRadius: 2,
            mt: 1,
            boxShadow: "0 8px 28px rgba(0,0,0,0.16)",
            overflow: "hidden",
          },
        }}
      >
        {/* header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>การแจ้งเตือน</Typography>
            {unreadCount > 0 && (
              <Box
                sx={{
                  bgcolor: "error.main",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 10,
                  px: 1,
                  py: "1px",
                  minWidth: 20,
                  textAlign: "center",
                }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </Box>
            )}
          </Box>
          <Button
            size="small"
            startIcon={<DoneAllOutlinedIcon />}
            onClick={markAllRead}
            disabled={unreadCount === 0}
            sx={{ textTransform: "none", fontSize: 13 }}
          >
            อ่านทั้งหมด
          </Button>
        </Box>
        <Divider />

        {/* body */}
        <Box sx={{ maxHeight: 440, overflowY: "auto" }}>
          {loading && items.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={26} />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
              <NotificationsNoneOutlinedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
              <Typography sx={{ mt: 1, fontSize: 14 }}>ยังไม่มีการแจ้งเตือน</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {items.map((n) => {
                const v = categoryVisual(n.Category);
                return (
                  <ListItemButton
                    key={n.NotificationId}
                    onClick={() => handleItemClick(n)}
                    alignItems="flex-start"
                    sx={{
                      px: 2,
                      py: 1.25,
                      gap: 1.25,
                      bgcolor: n.IsRead ? "transparent" : "rgba(25,118,210,0.06)",
                      "&:hover": { bgcolor: n.IsRead ? "action.hover" : "rgba(25,118,210,0.10)" },
                      borderBottom: "1px solid #f0f0f0",
                      alignItems: "center",
                    }}
                  >
                    <Avatar sx={{ bgcolor: v.bg, color: v.color, width: 38, height: 38 }}>
                      {v.icon}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          noWrap
                          sx={{ fontWeight: n.IsRead ? 500 : 700, fontSize: 14, flex: 1 }}
                        >
                          {n.Title || "การแจ้งเตือน"}
                        </Typography>
                        {!n.IsRead && (
                          <FiberManualRecordIcon sx={{ fontSize: 10, color: "primary.main" }} />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "text.secondary",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {n.Message}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, mt: 0.25 }}>
                        {n.ActorName && (
                          <Typography sx={{ fontSize: 12, color: "text.disabled" }} noWrap>
                            โดย {n.ActorName}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: 12, color: "text.disabled", ml: "auto" }} noWrap>
                          {timeAgo(n.CreateDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}
